import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setToastMessage } from "../../store/slices/app/appSlice";

export interface DestinationDraft {
  id: string;
  label: string;
  url: string;
  utm: string;
  isAutoUtm: boolean;
}

interface AssetDestinationsEditorProps {
  destinations: DestinationDraft[];
  onChange: (destinations: DestinationDraft[]) => void;
  /** Builds the automatic UTM for a destination, using the campaign context. */
  buildAutoUtm: (destination: DestinationDraft) => string;
  maxDestinations?: number;
}

export const createDestinationDraft = (
  overrides: Partial<DestinationDraft> = {}
): DestinationDraft => ({
  id:
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  label: "",
  url: "",
  utm: "",
  isAutoUtm: true,
  ...overrides,
});

const AssetDestinationsEditor: React.FC<AssetDestinationsEditorProps> = ({
  destinations,
  onChange,
  buildAutoUtm,
  maxDestinations = 25,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const updateDestination = (id: string, patch: Partial<DestinationDraft>) => {
    onChange(
      destinations.map((destination) =>
        destination.id === id ? { ...destination, ...patch } : destination
      )
    );
  };

  const handleAdd = () => {
    onChange([...destinations, createDestinationDraft()]);
  };

  const handleRemove = (id: string) => {
    onChange(destinations.filter((destination) => destination.id !== id));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= destinations.length) return;

    const next = [...destinations];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const handleCopy = (value: string) => {
    void navigator.clipboard.writeText(value);
    dispatch(
      setToastMessage({
        message: t("common.copied", "Copied to clipboard!"),
        severity: "success",
      })
    );
  };

  const canAddMore = destinations.length < maxDestinations;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LinkIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              {t("briefingsView.asset.destinations", "Destination URLs")}
            </Typography>
            <Chip
              size="small"
              label={destinations.length}
              sx={{ height: 20, fontWeight: 700 }}
            />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {t(
              "briefingsEdit.helper.destinations",
              "Add one row per landing page. The reference name identifies it and is used as utm_content."
            )}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddLinkIcon />}
          onClick={handleAdd}
          disabled={!canAddMore}
        >
          {t("briefingsEdit.form.addDestination", "Add destination")}
        </Button>
      </Stack>

      {destinations.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px dashed ${theme.palette.divider}`,
            bgcolor: theme.palette.action.hover,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              "briefingsEdit.form.noDestinations",
              "No destination URLs yet. Add the first one to generate its UTM."
            )}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddLinkIcon />}
            onClick={handleAdd}
          >
            {t("briefingsEdit.form.addDestination", "Add destination")}
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {destinations.map((destination, index) => {
            const autoUtm = buildAutoUtm(destination);
            const utmValue = destination.isAutoUtm ? autoUtm : destination.utm;
            const isPrimary = index === 0;

            return (
              <Paper
                key={destination.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${
                    isPrimary
                      ? theme.palette.primary.light
                      : theme.palette.divider
                  }`,
                  bgcolor: theme.palette.background.paper,
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[1] },
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: isPrimary
                          ? "primary.main"
                          : theme.palette.action.selected,
                        color: isPrimary
                          ? "primary.contrastText"
                          : "text.secondary",
                      }}
                    >
                      {index + 1}
                    </Box>

                    <TextField
                      size="small"
                      sx={{ flex: 1, minWidth: 200 }}
                      label={t(
                        "briefingsView.asset.destinationLabel",
                        "Reference name"
                      )}
                      placeholder={t(
                        "briefingsEdit.form.destinationLabelPlaceholder",
                        "e.g. Home, Promo landing, Form"
                      )}
                      value={destination.label}
                      onChange={(event) =>
                        updateDestination(destination.id, {
                          label: event.target.value,
                        })
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LabelOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {isPrimary && (
                      <Tooltip
                        title={t(
                          "briefingsEdit.form.primaryDestinationHint",
                          "Shown as the campaign's main destination. Reorder to change it."
                        )}
                      >
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={t(
                            "briefingsView.asset.primaryDestination",
                            "Main"
                          )}
                        />
                      </Tooltip>
                    )}

                    <Stack direction="row" spacing={0.5} sx={{ ml: "auto" }}>
                      <Tooltip title={t("common.moveUp", "Move up")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleMove(index, -1)}
                            disabled={index === 0}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("common.moveDown", "Move down")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleMove(index, 1)}
                            disabled={index === destinations.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("common.delete", "Delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemove(destination.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <TextField
                    fullWidth
                    size="small"
                    label={t(
                      "briefingsView.asset.destinationUrl",
                      "Destination URL"
                    )}
                    placeholder="https://..."
                    value={destination.url}
                    onChange={(event) =>
                      updateDestination(destination.id, {
                        url: event.target.value,
                      })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: destination.url.trim() ? (
                        <InputAdornment position="end">
                          <Tooltip title={t("common.open", "Open")}>
                            <IconButton
                              size="small"
                              component="a"
                              href={destination.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label={t("briefingsView.asset.utm", "UTM")}
                    value={utmValue}
                    disabled={destination.isAutoUtm}
                    onChange={(event) =>
                      updateDestination(destination.id, {
                        utm: event.target.value,
                        isAutoUtm: false,
                      })
                    }
                    helperText={
                      destination.isAutoUtm
                        ? t(
                            "briefingsEdit.helper.autoUtm",
                            "Generated automatically"
                          )
                        : t(
                            "briefingsEdit.helper.manualUtm",
                            "Manual UTM: it will not be regenerated"
                          )
                    }
                    InputProps={{
                      endAdornment: utmValue ? (
                        <InputAdornment position="end">
                          <Tooltip title={t("common.copy", "Copy")}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(utmValue)}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />

                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={destination.isAutoUtm}
                        onChange={(event) =>
                          updateDestination(destination.id, {
                            isAutoUtm: event.target.checked,
                            utm: event.target.checked ? autoUtm : utmValue,
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t("briefingsEdit.form.autoUtm", "Automatic UTM")}
                      </Typography>
                    }
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {!canAddMore && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("briefingsEdit.form.maxDestinations", {
            defaultValue: "You can add up to {{count}} destination URLs.",
            count: maxDestinations,
          })}
        </Alert>
      )}
    </Box>
  );
};

export default AssetDestinationsEditor;
